import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../assets/logo.gif';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import UpgradeModal from './UpgradeModal';

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, canAccess } = useAuth();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);

  const menuItems = [
    { path: '/', label: 'Home' },
    { path: '/education', label: 'Education' },
    { path: '/balances', label: 'Balances', requiresAuth: true },
    { path: '/exchanges', label: 'Exchanges' },
    { path: '/symbols', label: 'Symbols' },
    { path: '/funding-fees', label: 'Funding Rates' },
    { path: '/taker-maker', label: 'Taker/Maker' },
    { path: '/settings', label: 'Settings', requiresAuth: true },
  ];

  const strategyItems = [
    { path: '/opportunities', label: 'Opportunities', requiresAuth: true, feature: 'opportunities' },
    { path: '/trades', label: 'Trades', requiresAuth: true, feature: 'trades' },
  ];

  const notificationItems = [
    { path: '/notifications/funding-rate', label: 'Funding Rate Notifications', requiresAuth: true, feature: 'notifications' },
  ];

  const isAdmin = user?.role && user.role.split(';').map(r => r.trim().toLowerCase()).includes('admin');
  const adminItems = isAdmin ? [
    { path: '/admin/scripts', label: 'Scripts', requiresAuth: true },
    { path: '/admin/redis', label: 'Redis', requiresAuth: true },
    { path: '/admin/orderbook-streaming', label: 'Orderbook Streaming', requiresAuth: true },
    { path: '/admin/hedge-opportunities', label: 'Hedge Opportunities', requiresAuth: true },
  ] : [];

  const handleMenuClick = (item, e) => {
    if (item.requiresAuth && !user) {
      e.preventDefault();
      trackEvent('Navigation', 'Protected Page Click - Redirected to Login', item.label);
      navigate('/login');
      return;
    }

    // Logged in but plan doesn't allow this feature — show upgrade modal
    if (item.feature && user && !canAccess(item.feature)) {
      e.preventDefault();
      trackEvent('Navigation', 'Plan Locked Feature Click', item.label);
      setUpgradeModalOpen(true);
      return;
    }

    trackEvent('Navigation', 'Menu Click', item.label);
    setIsMobileMenuOpen(false);
  };

  const LockIcon = () => (
    <svg
      width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ marginLeft: '6px', verticalAlign: 'middle' }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );

  return (
    <>
      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src={logoImage} alt="Micropincher Logo" className="sidebar-logo-img" />
            <span>Micropincher</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {menuItems.map((item) => {
              const notLoggedIn = item.requiresAuth && !user;
              const planLocked = item.feature && user && !canAccess(item.feature);
              const isLocked = notLoggedIn || planLocked;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                    onClick={(e) => handleMenuClick(item, e)}
                    style={isLocked ? { color: '#8b949e', cursor: 'pointer', opacity: 0.6 } : {}}
                    title={planLocked ? 'Requires a paid plan' : undefined}
                  >
                    <span>
                      {item.label}
                      {isLocked && <LockIcon />}
                    </span>
                  </Link>
                </li>
              );
            })}

            {/* Funding Rate Strategy group */}
            <li>
              <button
                onClick={() => setStrategyOpen(!strategyOpen)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: '8px 16px', textAlign: 'left', cursor: 'pointer',
                  color: '#8b949e', fontSize: 'inherit', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <span>Funding Rate Strategy</span>
                <span style={{ fontSize: '10px', transition: 'transform 0.2s', display: 'inline-block', transform: strategyOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              </button>
            </li>

            {strategyOpen && strategyItems.map((item) => {
              const notLoggedIn = item.requiresAuth && !user;
              const planLocked = item.feature && user && !canAccess(item.feature);
              const isLocked = notLoggedIn || planLocked;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

              return (
                <li key={item.path} style={{ paddingLeft: '12px' }}>
                  <Link
                    to={item.path}
                    className={`${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                    onClick={(e) => handleMenuClick(item, e)}
                    style={isLocked ? { color: '#8b949e', cursor: 'pointer', opacity: 0.6 } : {}}
                    title={planLocked ? 'Requires a paid plan' : undefined}
                  >
                    <span>
                      {item.label}
                      {isLocked && <LockIcon />}
                    </span>
                  </Link>
                </li>
              );
            })}

            {/* Notifications group */}
            <li>
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  padding: '8px 16px', textAlign: 'left', cursor: 'pointer',
                  color: '#8b949e', fontSize: 'inherit', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between',
                }}
              >
                <span>Notifications</span>
                <span style={{ fontSize: '10px', transition: 'transform 0.2s', display: 'inline-block', transform: notificationsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
              </button>
            </li>

            {notificationsOpen && notificationItems.map((item) => {
              const notLoggedIn = item.requiresAuth && !user;
              const planLocked = item.feature && user && !canAccess(item.feature);
              const isLocked = notLoggedIn || planLocked;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

              return (
                <li key={item.path} style={{ paddingLeft: '12px' }}>
                  <Link
                    to={item.path}
                    className={`${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                    onClick={(e) => handleMenuClick(item, e)}
                    style={isLocked ? { color: '#8b949e', cursor: 'pointer', opacity: 0.6 } : {}}
                    title={planLocked ? 'Requires a paid plan' : undefined}
                  >
                    <span>
                      {item.label}
                      {isLocked && <LockIcon />}
                    </span>
                  </Link>
                </li>
              );
            })}

            {/* Admin group */}
            {isAdmin && (
              <>
                <li>
                  <button
                    onClick={() => setAdminOpen(!adminOpen)}
                    style={{
                      width: '100%', background: 'none', border: 'none',
                      padding: '8px 16px', textAlign: 'left', cursor: 'pointer',
                      color: '#8b949e', fontSize: 'inherit', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between',
                    }}
                  >
                    <span>Admin</span>
                    <span style={{ fontSize: '10px', transition: 'transform 0.2s', display: 'inline-block', transform: adminOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                  </button>
                </li>
                {adminOpen && adminItems.map((item) => {
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <li key={item.path} style={{ paddingLeft: '12px' }}>
                      <Link
                        to={item.path}
                        className={isActive ? 'active' : ''}
                        onClick={(e) => handleMenuClick(item, e)}
                      >
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </>
            )}
          </ul>
        </nav>
      </div>

      {isMobileMenuOpen && <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} />}
      {upgradeModalOpen && <UpgradeModal onClose={() => setUpgradeModalOpen(false)} />}
    </>
  );
};

export default Sidebar;
